import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as InfoIcon } from '../../assets/images/icons/info.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/ic-exit-rounded-purple.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { isPbmConfirmDisabled } from '../pbm/pbmHelpers';
import { WHATSAPP_PBM_DEFAULT_PROGRAM_NAME, WHATSAPP_PBM_SIDEBAR_TITLE } from './constants';

interface WhatsappPbmConsentStepProps {
  /** Nome exibido no texto (`Nome` do benefício, configurado no medicamento). */
  programName?: string;
  consentGiven: boolean;
  onConsentChange: (checked: boolean) => void;
  isLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const WhatsappPbmConsentStep: React.FC<WhatsappPbmConsentStepProps> = ({
  programName,
  consentGiven,
  onConsentChange,
  isLoading = false,
  onConfirm,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const isConfirmDisabled = isPbmConfirmDisabled(consentGiven, isLoading);
  const laboratoryName = programName?.trim() || WHATSAPP_PBM_DEFAULT_PROGRAM_NAME;

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
        {isMobile ? (
          <>
            <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
              <LeftArrowIcon onClick={onClose} cursor={'pointer'} />
              <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
                Voltar
              </Text>
            </Flex>
            <Flex flexDirection={'column'} padding={'0px 20px'} mb={'16px'}>
              <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
                {WHATSAPP_PBM_SIDEBAR_TITLE}
              </Text>
            </Flex>
          </>
        ) : (
          <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} padding={'20px'}>
            <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
              {WHATSAPP_PBM_SIDEBAR_TITLE}
            </Text>
            <CloseIcon onClick={onClose} cursor={'pointer'} />
          </Flex>
        )}
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Flex flexDirection={'column'} gap={'1px'}>
          <Text fontSize={'18px'} fontWeight={700} color={'primary.main'}>
            Confirmação de consentimento
          </Text>

          <Text fontSize={'14px'} color={'#343A40'} lineHeight={'17px'} paddingBottom={'18px'}>
            Para continuar, confirme que o paciente autorizou o envio de informações sobre o Programa de Desconto da{' '}
            {laboratoryName}:
          </Text>

          <Checkbox
            isChecked={consentGiven}
            onChange={(e) => onConsentChange(e.target.checked)}
            size="md"
            alignItems={'flex-start'}
          >
            <Box ml={'8px'} flex={1}>
              <Text fontSize={'14px'} color={'#343A40'} lineHeight={'20px'}>
                Declaro que obtive o consentimento do paciente e o informei de que receberá informações sobre o Programa
                de Desconto da {laboratoryName} no telefone cadastrado.
              </Text>
            </Box>
          </Checkbox>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'0px 20px 20px 20px'}>
        <Box padding={'16px'} backgroundColor={'#EEF1FF'} borderRadius={'10px'}>
          <Flex flexDirection={'row'} gap={'12px'}>
            <Box flexShrink={0}>
              <InfoIcon width={'16px'} height={'16px'} fill="#5C2E8C" />
            </Box>
            <Flex flexDirection={'column'} gap={'4px'}>
              <Text fontSize={'14px'} fontWeight={700} color={'#343A40'}>
                Próximos passos
              </Text>
              <Text fontSize={'14px'} color={'#343A40'}>
                O paciente receberá informações sobre o Programa de Desconto da {laboratoryName} e como obter o
                benefício.
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onClose}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={isConfirmDisabled}
            isLoading={isLoading}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            rightIcon={<RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" />}
          >
            Confirmar
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default WhatsappPbmConsentStep;
